import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CaretLeftIcon as CaretLeft } from "@phosphor-icons/react";

/**
 * Universal back affordance — rendered by both AppShell (authed) and
 * PublicShell. Shown on every page EXCEPT canonical "home" destinations
 * where Back has no useful target:
 *
 *   • /              landing page
 *   • /login, /register, /forgot-password, /reset-password
 *   • /dashboard     user's home
 *   • /admin         admin console (it has its own "Back to admin console"
 *                    pill on every sub-page rendered by AppShell)
 *
 * Behaviour:
 *   • First-visit (history length 1) → fall back to /dashboard for
 *     authed shells, / for public shells. Otherwise the back arrow
 *     would do nothing and surprise the user.
 *   • Otherwise: window.history.back() honors the browser stack the
 *     user actually built, so it works across deep-links and refreshes.
 *
 * Visual: small pill with a single caret-left icon, ink text on white,
 * gold-hairline accent on hover — same chrome as the existing
 * "Back to admin console" pill so it reads as one family.
 */
const HIDE_ON_EXACT = new Set([
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/dashboard",
  "/admin",
  // The Command Center is the admin home — it's reachable from both
  // /admin (Operations) and /dashboard via the gold mode switcher
  // pill. A "Back" here always landed on /admin (Operations), which
  // the operator perceived as "Back is broken — it just toggles me
  // to the wrong admin tab". Treat /admin/console the same way we
  // treat /dashboard and /admin: a home destination with no useful
  // Back target. (Reported by Lauren 2026-05-28.)
  "/admin/console",
]);

// Hide on dynamic marketing reel screens too — those are intended for
// recording/embedding, not navigation.
const HIDE_PREFIX = ["/marketing/reel/"];

export default function BackButton({ fallback = "/" }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (HIDE_ON_EXACT.has(pathname)) return null;
  if (HIDE_PREFIX.some((p) => pathname.startsWith(p))) return null;

  const onClick = (e) => {
    e.preventDefault();
    // If the user opened this page from outside (e.g. a deep link or
    // refresh), history.length will be 1 — going back would just leave
    // the app. Send them to the configured fallback instead.
    if (window.history.length <= 1) {
      navigate(fallback);
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="back-button"
      aria-label="Back"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-2 min-h-[40px] text-[12px] font-bold text-ink hover:border-[#0E0E0E] hover:bg-[#FAFAFA] transition-colors mb-5"
    >
      <CaretLeft size={12} weight="bold" />
      Back
    </button>
  );
}
